import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import TagBadge from '../components/TagBadge';

const COLOR_PALETTE = [
  { hex: '#ef4444', label: 'Red' },
  { hex: '#f97316', label: 'Orange' },
  { hex: '#f59e0b', label: 'Amber' },
  { hex: '#84cc16', label: 'Lime' },
  { hex: '#22c55e', label: 'Green' },
  { hex: '#14b8a6', label: 'Teal' },
  { hex: '#06b6d4', label: 'Cyan' },
  { hex: '#3b82f6', label: 'Blue' },
  { hex: '#6366f1', label: 'Indigo' },
  { hex: '#8b5cf6', label: 'Violet' },
  { hex: '#d946ef', label: 'Fuchsia' },
  { hex: '#ec4899', label: 'Pink' },
  { hex: '#6b7280', label: 'Gray' },
  { hex: '#78716c', label: 'Stone' },
];

const ENTITY_TYPES = [
  { value: 'patient', label: 'Patient' },
  { value: 'provider', label: 'Provider' },
  { value: 'appointment_type', label: 'Appointment Type' },
  { value: 'appointment', label: 'Appointment' },
];

function TagManagement({ practiceId }) {
  const [activeTab, setActiveTab] = useState('patient');
  const [definitions, setDefinitions] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ text: '', type: '' });

  // Form state for new tag
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', color: '#3b82f6', description: '', tag_group_id: '' });
  const [editingId, setEditingId] = useState(null);

  // Group form
  const [showGroupForm, setShowGroupForm] = useState(false);
  const [groupForm, setGroupForm] = useState({ name: '', description: '' });
  const [editingGroupId, setEditingGroupId] = useState(null);

  const showMessage = (text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 3000);
  };

  const loadData = useCallback(async () => {
    if (!practiceId) return;
    try {
      setLoading(true);
      const [defsRes, groupsRes] = await Promise.all([
        api.getTagDefinitions(practiceId, activeTab, true),
        api.getTagGroups(practiceId),
      ]);
      if (defsRes.success) setDefinitions(defsRes.data || []);
      if (groupsRes.success) setGroups(groupsRes.data || []);
    } catch (err) {
      console.error('Error loading tags:', err);
    } finally {
      setLoading(false);
    }
  }, [practiceId, activeTab]);

  useEffect(() => { loadData(); }, [loadData]);

  // ── Tag Definition CRUD ──

  const resetForm = () => {
    setForm({ name: '', color: '#3b82f6', description: '', tag_group_id: '' });
    setEditingId(null);
    setShowForm(false);
  };

  const handleSaveTag = async () => {
    if (!form.name.trim()) return showMessage('Tag name is required', 'error');
    try {
      if (editingId) {
        const res = await api.updateTagDefinition(practiceId, editingId, form);
        if (res.success) showMessage('Tag updated');
      } else {
        const res = await api.createTagDefinition(practiceId, {
          ...form, entity_type: activeTab, tag_group_id: form.tag_group_id || null,
        });
        if (res.success) showMessage('Tag created');
        else showMessage(res.error || 'Failed to create tag', 'error');
      }
      resetForm();
      loadData();
    } catch (err) {
      showMessage(err.message || 'Error saving tag', 'error');
    }
  };

  const handleEditTag = (def) => {
    setForm({ name: def.name, color: def.color, description: def.description || '', tag_group_id: def.tag_group_id || '' });
    setEditingId(def.id);
    setShowForm(true);
  };

  const handleDeleteTag = async (id) => {
    const def = definitions.find(d => d.id === id);
    if (def?.is_system) {
      showMessage('System tags cannot be deleted. Use the toggle to disable instead.', 'error');
      return;
    }
    if (!window.confirm('Delete this tag? If it is assigned to entities, it will be deactivated instead.')) return;
    try {
      const res = await api.deleteTagDefinition(practiceId, id);
      if (res.success) showMessage(res.message || 'Tag deleted');
      loadData();
    } catch (err) {
      showMessage(err.message || 'Error deleting tag', 'error');
    }
  };

  const handleToggleSystemTag = async (def) => {
    try {
      const newActive = def.is_active === 1 ? 0 : 1;
      const res = await api.updateTagDefinition(practiceId, def.id, { is_active: newActive });
      if (res.success) showMessage(newActive ? 'AI tag enabled' : 'AI tag disabled');
      loadData();
    } catch (err) {
      showMessage(err.message || 'Error toggling tag', 'error');
    }
  };

  // ── Tag Group CRUD ──

  const resetGroupForm = () => {
    setGroupForm({ name: '', description: '' });
    setEditingGroupId(null);
    setShowGroupForm(false);
  };

  const handleSaveGroup = async () => {
    if (!groupForm.name.trim()) return showMessage('Group name is required', 'error');
    try {
      if (editingGroupId) {
        await api.updateTagGroup(practiceId, editingGroupId, groupForm);
        showMessage('Group updated');
      } else {
        const res = await api.createTagGroup(practiceId, groupForm);
        if (res.success) showMessage('Group created');
        else showMessage(res.error || 'Failed to create group', 'error');
      }
      resetGroupForm();
      loadData();
    } catch (err) {
      showMessage(err.message || 'Error saving group', 'error');
    }
  };

  const handleDeleteGroup = async (id) => {
    if (!window.confirm('Delete this tag group? Tags in this group will become ungrouped.')) return;
    try {
      await api.deleteTagGroup(practiceId, id);
      showMessage('Group deleted');
      loadData();
    } catch (err) {
      showMessage(err.message || 'Error deleting group', 'error');
    }
  };

  if (!practiceId) return <div style={{ padding: '2rem' }}>Select a practice first.</div>;

  return (
    <div style={{ padding: '1.5rem', maxWidth: 900 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 600 }}>Tags & Labels</h2>
        {message.text && (
          <span style={{
            padding: '6px 12px', borderRadius: 6, fontSize: 13,
            background: message.type === 'error' ? '#fef2f2' : '#f0fdf4',
            color: message.type === 'error' ? '#dc2626' : '#16a34a',
            border: `1px solid ${message.type === 'error' ? '#fecaca' : '#bbf7d0'}`,
          }}>{message.text}</span>
        )}
      </div>

      {/* Entity Type Tabs */}
      <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid #e5e7eb', marginBottom: '1.5rem' }}>
        {ENTITY_TYPES.map(et => (
          <button
            key={et.value}
            onClick={() => { setActiveTab(et.value); resetForm(); }}
            style={{
              padding: '8px 16px', fontSize: 13, fontWeight: 500,
              background: 'none', border: 'none', cursor: 'pointer',
              borderBottom: activeTab === et.value ? '2px solid #3b82f6' : '2px solid transparent',
              color: activeTab === et.value ? '#1d4ed8' : '#6b7280',
              marginBottom: -2,
            }}
          >
            {et.label}
          </button>
        ))}
      </div>

      {loading ? (
        <p style={{ color: '#94a3b8' }}>Loading tags...</p>
      ) : (
        <>
          {/* Tag Groups Section */}
          <div style={{ marginBottom: '2rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: '#374151' }}>Tag Groups</h3>
              <button
                onClick={() => { setShowGroupForm(!showGroupForm); if (showGroupForm) resetGroupForm(); }}
                style={{
                  padding: '5px 12px', fontSize: 12, borderRadius: 6,
                  border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer', color: '#374151',
                }}
              >
                {showGroupForm ? 'Cancel' : '+ New Group'}
              </button>
            </div>

            {showGroupForm && (
              <div style={{
                display: 'flex', gap: 8, alignItems: 'flex-end', marginBottom: 12,
                padding: 12, background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0',
              }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 11, color: '#64748b', display: 'block', marginBottom: 2 }}>Name</label>
                  <input
                    value={groupForm.name}
                    onChange={e => setGroupForm(f => ({ ...f, name: e.target.value }))}
                    placeholder="e.g. Customer Quality"
                    style={{ width: '100%', padding: '6px 8px', fontSize: 13, border: '1px solid #d1d5db', borderRadius: 4 }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 11, color: '#64748b', display: 'block', marginBottom: 2 }}>Description</label>
                  <input
                    value={groupForm.description}
                    onChange={e => setGroupForm(f => ({ ...f, description: e.target.value }))}
                    placeholder="Optional description"
                    style={{ width: '100%', padding: '6px 8px', fontSize: 13, border: '1px solid #d1d5db', borderRadius: 4 }}
                  />
                </div>
                <button
                  onClick={handleSaveGroup}
                  style={{
                    padding: '6px 14px', fontSize: 12, borderRadius: 6,
                    border: 'none', background: '#3b82f6', color: '#fff', cursor: 'pointer', fontWeight: 500,
                  }}
                >
                  {editingGroupId ? 'Update' : 'Create'}
                </button>
              </div>
            )}

            {groups.length === 0 ? (
              <p style={{ fontSize: 13, color: '#94a3b8', margin: '4px 0' }}>No tag groups yet. Groups help organize tags.</p>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {groups.map(g => (
                  <div key={g.id} style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '4px 10px', borderRadius: 6, background: '#f1f5f9',
                    border: '1px solid #e2e8f0', fontSize: 13,
                  }}>
                    <span style={{ fontWeight: 500 }}>{g.name}</span>
                    <span
                      onClick={() => { setGroupForm({ name: g.name, description: g.description || '' }); setEditingGroupId(g.id); setShowGroupForm(true); }}
                      style={{ cursor: 'pointer', color: '#3b82f6', fontSize: 11 }}
                      title="Edit group"
                    >edit</span>
                    <span
                      onClick={() => handleDeleteGroup(g.id)}
                      style={{ cursor: 'pointer', color: '#ef4444', fontSize: 11 }}
                      title="Delete group"
                    >&times;</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Tag Definitions Section */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: '#374151' }}>
                {ENTITY_TYPES.find(e => e.value === activeTab)?.label} Tags
              </h3>
              <button
                onClick={() => { setShowForm(!showForm); if (showForm) resetForm(); }}
                style={{
                  padding: '5px 12px', fontSize: 12, borderRadius: 6,
                  border: '1px solid #3b82f6', background: '#3b82f6', color: '#fff',
                  cursor: 'pointer', fontWeight: 500,
                }}
              >
                {showForm ? 'Cancel' : '+ New Tag'}
              </button>
            </div>

            {showForm && (
              <div style={{
                padding: 16, background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0',
                marginBottom: 16,
              }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                  <div>
                    <label style={{ fontSize: 11, color: '#64748b', display: 'block', marginBottom: 2 }}>Tag Name *</label>
                    <input
                      value={form.name}
                      onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                      placeholder="e.g. VIP, High Risk, New Patient"
                      style={{ width: '100%', padding: '6px 8px', fontSize: 13, border: '1px solid #d1d5db', borderRadius: 4 }}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: 11, color: '#64748b', display: 'block', marginBottom: 2 }}>Group (optional)</label>
                    <select
                      value={form.tag_group_id}
                      onChange={e => setForm(f => ({ ...f, tag_group_id: e.target.value }))}
                      style={{ width: '100%', padding: '6px 8px', fontSize: 13, border: '1px solid #d1d5db', borderRadius: 4 }}
                    >
                      <option value="">No group</option>
                      {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                    </select>
                  </div>
                </div>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ fontSize: 11, color: '#64748b', display: 'block', marginBottom: 2 }}>Description</label>
                  <input
                    value={form.description}
                    onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                    placeholder="Brief description of when to use this tag"
                    style={{ width: '100%', padding: '6px 8px', fontSize: 13, border: '1px solid #d1d5db', borderRadius: 4 }}
                  />
                </div>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ fontSize: 11, color: '#64748b', display: 'block', marginBottom: 4 }}>Color</label>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {COLOR_PALETTE.map(c => (
                      <button
                        key={c.hex}
                        onClick={() => setForm(f => ({ ...f, color: c.hex }))}
                        title={c.label}
                        style={{
                          width: 28, height: 28, borderRadius: '50%', border: form.color === c.hex ? '3px solid #1e293b' : '2px solid #e2e8f0',
                          background: c.hex, cursor: 'pointer', padding: 0,
                        }}
                      />
                    ))}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <button
                    onClick={handleSaveTag}
                    style={{
                      padding: '6px 16px', fontSize: 13, borderRadius: 6,
                      border: 'none', background: '#3b82f6', color: '#fff', cursor: 'pointer', fontWeight: 500,
                    }}
                  >
                    {editingId ? 'Update Tag' : 'Create Tag'}
                  </button>
                  <span style={{ fontSize: 13, color: '#64748b' }}>Preview:</span>
                  <TagBadge name={form.name || 'Tag Name'} color={form.color} size="medium" />
                </div>
              </div>
            )}

            {definitions.length === 0 ? (
              <div style={{
                padding: '2rem', textAlign: 'center', color: '#94a3b8',
                border: '1px dashed #d1d5db', borderRadius: 8,
              }}>
                <p style={{ margin: '0 0 8px', fontSize: 14 }}>No tags defined for {ENTITY_TYPES.find(e => e.value === activeTab)?.label}s yet.</p>
                <p style={{ margin: 0, fontSize: 12 }}>Click "+ New Tag" to create your first tag.</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {definitions.map(def => (
                  <div key={def.id} style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '10px 14px', borderRadius: 8,
                    border: def.is_system ? '1px solid #c7d2fe' : '1px solid #e2e8f0',
                    background: def.is_system ? '#f5f3ff' : '#fff',
                    opacity: def.is_active === 0 ? 0.55 : 1,
                  }}>
                    <TagBadge name={def.name} color={def.color} size="medium" />
                    {def.is_system === 1 && (
                      <span style={{
                        fontSize: 10, fontWeight: 600, color: '#6366f1', background: '#e0e7ff',
                        padding: '1px 6px', borderRadius: 4, letterSpacing: 0.5,
                      }}>
                        AI
                      </span>
                    )}
                    {def.group_name && (
                      <span style={{ fontSize: 11, color: '#94a3b8', background: '#f8fafc', padding: '1px 6px', borderRadius: 4 }}>
                        {def.group_name}
                      </span>
                    )}
                    {def.description && (
                      <span style={{ fontSize: 12, color: '#64748b', flex: 1 }}>{def.description}</span>
                    )}
                    {!def.description && <span style={{ flex: 1 }} />}
                    {def.is_active === 0 && (
                      <span style={{ fontSize: 11, color: '#94a3b8', fontStyle: 'italic' }}>Disabled</span>
                    )}
                    <button
                      onClick={() => handleEditTag(def)}
                      style={{
                        padding: '3px 10px', fontSize: 11, borderRadius: 4,
                        border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer', color: '#374151',
                      }}
                    >
                      Edit
                    </button>
                    {def.is_system === 1 ? (
                      <button
                        onClick={() => handleToggleSystemTag(def)}
                        style={{
                          padding: '3px 10px', fontSize: 11, borderRadius: 4,
                          border: `1px solid ${def.is_active === 1 ? '#fed7aa' : '#bbf7d0'}`,
                          background: '#fff', cursor: 'pointer',
                          color: def.is_active === 1 ? '#ea580c' : '#16a34a',
                        }}
                      >
                        {def.is_active === 1 ? 'Disable' : 'Enable'}
                      </button>
                    ) : (
                      <button
                        onClick={() => handleDeleteTag(def.id)}
                        style={{
                          padding: '3px 10px', fontSize: 11, borderRadius: 4,
                          border: '1px solid #fecaca', background: '#fff', cursor: 'pointer', color: '#ef4444',
                        }}
                      >
                        Delete
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default TagManagement;
